import { useCallback, useMemo } from 'react'
import { CalendarClock, Power, PowerOff } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchScheduled, toggleScheduled, AUTO_STR as S, type ScheduledTaskRow } from './automationApi'

/**
 * A26.07 — Запланированные задачи (cron). Enable/disable a task. Dormant until
 * `/admin/automation/scheduled` ships. Placeholder permission USERS_VIEW.
 */
export function ScheduledTasksScreen() {
  const { toast } = useToast()

  const columns = useMemo<AdminColumn<ScheduledTaskRow>[]>(
    () => [
      { key: 'name', header: S.name, type: 'text' },
      { key: 'schedule', header: S.schedule, type: 'text' },
      { key: 'enabled', header: S.enabled, type: 'text' },
      { key: 'next_run', header: S.nextRun, type: 'text' },
      { key: 'last_run', header: S.lastRun, type: 'text' },
    ],
    [],
  )

  const toggle = useCallback(
    async (row: ScheduledTaskRow) => {
      try {
        await toggleScheduled(row.id, !row.enabled)
        toast(S.toggled, { tone: 'success' })
      } catch {
        toast(S.failed, { tone: 'error' })
      }
    },
    [toast],
  )

  const rowActions = useMemo<RowAction<ScheduledTaskRow>[]>(
    () => [
      {
        id: 'disable',
        label: S.disable,
        icon: <PowerOff size={14} />,
        danger: true,
        visible: (r) => r.enabled,
        onSelect: toggle,
      },
      {
        id: 'enable',
        label: S.enable,
        icon: <Power size={14} />,
        visible: (r) => !r.enabled,
        onSelect: toggle,
      },
    ],
    [toggle],
  )

  const fetcher = useCallback(fetchScheduled, [])
  return (
    <>
      <PageHeader title={S.scheduled} screenId="A26.07" />
      <DataTable<ScheduledTaskRow>
        screenId="A26.07"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <CalendarClock size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
